import { useEffect, useState } from "react";
import { IconSearch } from "@tabler/icons-react";
import {
  Group,
  Pagination,
  ScrollArea,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { RowData, RowHeader } from "../../types/types";
import TableRow from "./TableRow/TableRow";
import TableHeader from "./TableHeader/TableHeader";
import FilterGroup from "./FilterGroup/FilterGroup";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import AddCard from "./AddCard/AddCard";
import { CardSection } from "@mantine/core";

function filterData(data: RowData[], search: string) {
  const query = search.toLowerCase().trim();
  if (data.length === 0 || !data[0]) return [];

  return data.filter((item) =>
    Object.keys(item).some((key) => {
      // The download key contains the invoice ID to send to the
      // back end if the user wants to download it, and should not be seached over.
      if (key === "Download") return false;

      const value = item[key as keyof RowData]; // Ensure proper typing
      return value && String(value).toLowerCase().includes(query);
    })
  );
}

function sortData(
  data: RowData[] | undefined,
  payload: { sortBy: keyof RowData | null; reversed: boolean; search: string }
) {
  if (!Array.isArray(data) || data.length === 0) {
    return []; // Return an empty array if data is undefined or empty
  }

  const { sortBy } = payload;
  console.log("Data is ", data);

  if (!sortBy) {
    return filterData(data, payload.search);
  }

  return filterData(
    [...data].sort((a, b) => {
      if (payload.reversed) {
        return String(b[sortBy]).localeCompare(String(a[sortBy]));
      }
      return String(a[sortBy]).localeCompare(String(b[sortBy]));
    }),
    payload.search
  );
}

export function TableSort({ type }: { type: string }) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<keyof RowData | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [activePage, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterLoad, setFilterLoad] = useState(false);
  // Holds the original data from the url, which is passed into sortedData when the user starts searching or sorting.
  const [data, setData] = useState<RowData[]>([]);

  // Setup URL and headers based on type.
  const urlPath = (() => {
    switch (type) {
      case "invs":
        return "invoices";
      case "mandts":
        return "mandates";
      case "cards":
        return "cards";
      default:
        return "";
    }
  })();

  useEffect(() => {
    async function fetchTableItems() {
      setLoading(!false); // fok u Luke

      try {
        const response = await fetch(
          window.location.origin + "/server/client_service/" + urlPath
        );
        let resData = await response.json();
        console.log("Res data is", resData);

        if (!response.ok) {
          throw new Error("Failed to fetch items");
        }

        // json with column names and items as two arrays
        setData(resData);
        setSortedData(resData);
        setLoading(false);
      } catch (error) {
        console.log(error);
      }
    }
    if (urlPath !== undefined) {
      fetchTableItems();
    }
  }, [urlPath]);

  const [sortedData, setSortedData] = useState<RowData[]>([]);
  let dataHeaders: RowHeader<RowData>[] = [];
  switch (type) {
    case "invs":
      dataHeaders = [
        { displayName: "Number", key: "Number" },
        { displayName: "Date", key: "Date" },
        { displayName: "Status", key: "Status" },
        { displayName: "Amount", key: "Total" },
      ];

      break;
    case "mandts":
      dataHeaders = [
        { displayName: "Mandate Name", key: "mandate_name" },
        { displayName: "State", key: "state" },
        { displayName: "Reference", key: "reference" },
        { displayName: "Amount", key: "recurring_amount" },
      ];
      break;
    case "cards":
      dataHeaders = [
        { displayName: "Card ID", key: "card_id" },
        { displayName: "Last Four Digits", key: "last_four_digits" },
        { displayName: "Expiry Year", key: "expiry_year" },
        { displayName: "Expiry Month", key: "expiry_month" },        
      ];
      break;

    default:
      break;
  }

  // Pagination Vars
  const PAGE_SIZE = 10;
  const start = (activePage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  console.log("sortedData", sortedData);
  const paginatedData = sortedData.slice(start, end);
  console.log("paginatedData", paginatedData);


  const rows = paginatedData.map((row, index) => (
    <TableRow key={index} row={row} headers={dataHeaders} type={type} />
  ));

  // Set filter group props
  const labels = [];
  if (type === "invs") {
    labels.push("Number");
  } else if (type === "cards") {
    labels.push("Card", "Type");
  } else if (type === "mandts") {
    labels.push("Name", "State", "Reference", "Amount");
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearch(value);
    setSortedData(
      sortData(data, { sortBy, reversed: reverseSortDirection, search: value })
    );
  };

  const setSorting = (field: keyof RowData) => {
    console.log("set sorting");
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(data, { sortBy: field, reversed, search }));
  };

  function triggerLoad(state: boolean) {
    console.log("setting filter load to: " + state);
    setFilterLoad(state);
  }

  return (
    <>
      {loading ? (
        <LoadingSpinner size="lg" color="blue" />
      ) : (
        <ScrollArea>
          <Group justify="space-between">
            <FilterGroup
              labels={labels}
              type={type}
              setLoading={triggerLoad}
              setSortedData={setSortedData}
              handleSearchChange={handleSearchChange}
              search={search}
            />
            {type === "cards" ? <AddCard /> : null}
          </Group>
          {!filterLoad ? (
            <Table
              horizontalSpacing="md"
              verticalSpacing="xs"
              miw={700}
              layout="fixed"
            >
              <Table.Tbody>
                <Table.Tr>
                  {dataHeaders.length > 0
                    ? dataHeaders.map((header) => (
                        <TableHeader
                          key={header.displayName}
                          sorted={sortBy === header.displayName}
                          reversed={reverseSortDirection}
                          onSort={() => setSorting(header.key)}
                        >
                          {header.displayName}
                        </TableHeader>
                      ))
                    : null}

                  {type !== "cards" ? (
                    // All the Table componenets except cards have an extra row for buttons.
                    <TableHeader
                      key={"Action"}
                      sorted={false}
                      reversed={reverseSortDirection}
                      onSort={() => setSorting("Action")}
                    >
                      Action
                    </TableHeader>
                  ) : null}
                </Table.Tr>
              </Table.Tbody>
              <Table.Tbody>
                {rows.length > 0 ? (
                  rows
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={dataHeaders.length}>
                      <Text fw={500} ta="center">
                        Nothing found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          ) : (
            <LoadingSpinner size="lg" color="blue" />
          )}

          <Pagination
            total={Math.ceil(sortedData.length / PAGE_SIZE)}
            value={activePage}
            onChange={setPage}
            mt="md"
          />
        </ScrollArea>
      )}
    </>
  );
}
